// k:env-boundary — executor resolves the Anthropic API key from the environment (BE-005).
import type { DispatchedJob } from '@whittlelabs/jobs';
import {
  renderPromptExecution,
  readPromptExecutionSpec,
  pickProviderHints,
  PROMPT_EXECUTION_STRATEGY,
} from '@whittlelabs/loom';
import { KeepClient, ServiceTokenManager } from '@whittlelabs/keep';
import { resolveEnvRef, resolveConfigStringOrEnv } from '../config/env-ref';
import { assertOutputValid } from './validate-output';
import type { CostHint, ExecutorCapability, ExecutorResponse, JobExecutor } from './types';

/**
 * Anthropic Messages-API executor.
 *
 * Two key-resolution modes share the same dispatch path:
 *
 *  1. **Static** (default): YAML names the key directly, optionally via
 *     `${ENV_VAR}`.
 *  2. **Dynamic** (`apiKeyFrom: 'job-metadata'`): the hosted-Sifter tier. The
 *     executor reads `customerId` from the job's
 *     `providerHints.anthropicApi.customerId`, exchanges its service-identity
 *     credentials for a Keep token, reveals the customer's key, and dispatches
 *     with it. The cleartext key lives only for the outbound fetch.
 */

const DEFAULT_BASE_URL = 'https://api.anthropic.com/v1/messages';
const DEFAULT_MODEL = 'claude-sonnet-4-6';
const DEFAULT_MAX_TOKENS = 4096;
const DEFAULT_TIMEOUT_MS = 120_000;
const ANTHROPIC_API_VERSION = '2023-06-01';

type KeyResolver = (providerHints: Record<string, unknown>) => Promise<string>;

interface AnthropicApiInstance {
  resolveKey: KeyResolver;
  baseUrl: string;
  model: string;
  maxTokens: number;
  timeoutMs: number;
  capabilityId: string;
}

class AnthropicApiExecutor implements JobExecutor {
  readonly capability: ExecutorCapability;
  readonly strategies = [PROMPT_EXECUTION_STRATEGY];
  private readonly instance: AnthropicApiInstance;

  constructor(instance: AnthropicApiInstance) {
    this.instance = instance;
    this.capability = { id: instance.capabilityId };
  }

  estimate(dispatch: DispatchedJob): CostHint {
    const spec = readPromptExecutionSpec(dispatch.inputs);
    return {
      inputTokens: spec.costCapHint?.estimatedInputTokens,
      outputTokens: spec.costCapHint?.estimatedOutputTokens,
    };
  }

  async execute(dispatch: DispatchedJob, signal: AbortSignal): Promise<ExecutorResponse> {
    const { prompt, spec } = renderPromptExecution(dispatch.inputs);
    const outputLabel = spec.outputLabel;
    const providerHints = pickProviderHints(spec, this.instance.capabilityId);
    const apiKey = await this.instance.resolveKey(providerHints);
    // Per-job model override: a `providerHints.anthropicApi.model` on the
    // job wins over the static YAML model, so one running Sifter serves
    // every offered model (hosted multi-model configurations ride this).
    const hintedModel = (providerHints as { model?: unknown }).model;
    const body = {
      model:
        typeof hintedModel === 'string' && hintedModel.length > 0
          ? hintedModel
          : this.instance.model,
      max_tokens: this.instance.maxTokens,
      messages: [{ role: 'user', content: prompt }],
    };

    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), this.instance.timeoutMs);
    function onAbort(): void {
      timeoutController.abort();
    }
    signal.addEventListener('abort', onAbort, { once: true });

    try {
      const response = await fetch(this.instance.baseUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': ANTHROPIC_API_VERSION,
        },
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });

      if (!response.ok) {
        let bodyText = '';
        try {
          bodyText = await response.text();
        } catch {
          // ignore
        }
        // Anthropic wraps errors in a JSON envelope; unwrap it so the
        // failure reason reads as a sentence, not a JSON dump — this
        // string travels all the way to the product UI via job.failed.
        const envelope = bodyText ? safeJsonParse(bodyText) : undefined;
        const apiErr = envelope?.error as
          | { type?: string; message?: string }
          | undefined;
        if (apiErr?.message) {
          const kind = apiErr.type ? ` (${apiErr.type})` : '';
          throw new Error(
            `Anthropic API error${kind}: ${apiErr.message} [HTTP ${response.status}]`,
          );
        }
        throw new Error(`HTTP ${response.status}${bodyText ? `: ${bodyText}` : ''}`);
      }

      const data = (await response.json()) as Record<string, unknown>;
      const content = data.content as Array<Record<string, unknown>> | undefined;
      const rawText = (content?.[0]?.text as string) ?? '';
      const u = data.usage as { input_tokens?: number; output_tokens?: number } | undefined;
      const usage = u
        ? {
            inputTokens: u.input_tokens,
            outputTokens: u.output_tokens,
            aiProvider: 'anthropic',
            aiModel: this.instance.model,
          }
        : undefined;

      const parsed = safeJsonParse(rawText);
      assertOutputValid(spec, parsed);
      return {
        outputs: [{ label: outputLabel, content: parsed ?? { raw: rawText } }],
        rawText,
        parsed,
        ...(usage ? { usage } : {}),
      };
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }
}

/**
 * Optional dependency hook for the dynamic-key path. Production uses real
 * managers; tests inject stubs so they don't mock global fetch for the Keep call.
 */
export interface AnthropicApiDeps {
  buildServiceTokenManager?: (config: {
    keepApiUrl: string;
    clientId: string;
    clientSecret: string;
  }) => Pick<ServiceTokenManager, 'getToken'>;
  buildKeepClient?: (config: {
    baseUrl: string;
    serviceToken: string;
  }) => Pick<KeepClient, 'revealManagedSifterKey'>;
}

export function createAnthropicApiExecutor(
  config: Record<string, unknown>,
  capabilityId = 'anthropic-api',
  deps: AnthropicApiDeps = {},
): AnthropicApiExecutor {
  const rawApiKeyFrom = config.apiKeyFrom;
  const rawApiKey = config.apiKey;

  if (rawApiKeyFrom !== undefined && rawApiKey !== undefined) {
    throw new Error(
      'AnthropicApiExecutor accepts either "apiKey" (static) or "apiKeyFrom" (dynamic), not both',
    );
  }

  const resolveKey: KeyResolver =
    rawApiKeyFrom !== undefined
      ? buildDynamicResolver(config, deps)
      : buildStaticResolver(rawApiKey);

  let baseUrl = DEFAULT_BASE_URL;
  if (config.baseUrl !== undefined) {
    if (typeof config.baseUrl !== 'string' || config.baseUrl.length === 0) {
      throw new Error('"baseUrl" must be a non-empty string when set');
    }
    baseUrl = config.baseUrl;
  }

  let model = DEFAULT_MODEL;
  if (config.model !== undefined) {
    if (typeof config.model !== 'string' || config.model.length === 0) {
      throw new Error('"model" must be a non-empty string when set');
    }
    model = config.model;
  }

  let maxTokens = DEFAULT_MAX_TOKENS;
  if (config.maxTokens !== undefined) {
    if (typeof config.maxTokens !== 'number' || config.maxTokens <= 0) {
      throw new Error('"maxTokens" must be a positive number');
    }
    maxTokens = config.maxTokens;
  }

  let timeoutMs = DEFAULT_TIMEOUT_MS;
  if (config.timeout !== undefined) {
    if (typeof config.timeout !== 'number' || config.timeout <= 0) {
      throw new Error('"timeout" must be a positive number');
    }
    timeoutMs = config.timeout;
  }

  return new AnthropicApiExecutor({
    resolveKey,
    baseUrl,
    model,
    maxTokens,
    timeoutMs,
    capabilityId,
  });
}

function buildStaticResolver(rawApiKey: unknown): KeyResolver {
  if (typeof rawApiKey !== 'string' || rawApiKey.length === 0) {
    throw new Error(
      'AnthropicApiExecutor requires a non-empty "apiKey" string in config (or "apiKeyFrom: \\"job-metadata\\"" for dynamic key mode)',
    );
  }
  const resolved = resolveEnvRef(rawApiKey, 'apiKey');
  return async () => resolved;
}

function buildDynamicResolver(
  config: Record<string, unknown>,
  deps: AnthropicApiDeps,
): KeyResolver {
  if (config.apiKeyFrom !== 'job-metadata') {
    throw new Error(
      `Unsupported "apiKeyFrom" value: ${JSON.stringify(config.apiKeyFrom)}. Only "job-metadata" is supported in v0.2.`,
    );
  }
  const keepApiUrl = resolveConfigStringOrEnv(config, 'keepApiUrl', 'KEEP_API_URL');
  const clientId = resolveConfigStringOrEnv(config, 'keepClientId', 'KEEP_CLIENT_ID');
  const clientSecret = resolveConfigStringOrEnv(config, 'keepClientSecret', 'KEEP_CLIENT_SECRET');

  const stmFactory =
    deps.buildServiceTokenManager ?? ((cfg) => new ServiceTokenManager(cfg));
  const stm = stmFactory({ keepApiUrl, clientId, clientSecret });

  const keepClientFactory = deps.buildKeepClient ?? ((cfg) => new KeepClient(cfg));

  return async (providerHints) => {
    const hints = (providerHints ?? {}) as { customerId?: string } & Record<string, unknown>;
    const customerId = typeof hints.customerId === 'string' ? hints.customerId : undefined;
    if (!customerId) {
      throw new Error(
        'AnthropicApiExecutor dynamic-key mode requires providerHints.customerId on every job',
      );
    }
    const serviceToken = await stm.getToken();
    if (!serviceToken) {
      throw new Error(
        'AnthropicApiExecutor dynamic-key mode failed to exchange a Keep service token. Check KEEP_CLIENT_ID / KEEP_CLIENT_SECRET.',
      );
    }
    const keep = keepClientFactory({ baseUrl: keepApiUrl, serviceToken });
    const reveal = await keep.revealManagedSifterKey(customerId);
    return reveal.apiKey;
  };
}

function safeJsonParse(s: string): Record<string, unknown> | undefined {
  try {
    const parsed = JSON.parse(s);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}
